import QuizStep from "@/icons/products/oestra/pcos/products/server.svg";
import ReceiveTreatmentStep from "@/icons/products/oestra/pcos/products/gift.svg";
import StartTreatmentStep from "@/icons/products/oestra/pcos/products/bottle.svg";

import {
    CheckCircleIcon,
    FadersHorizontalIcon,
    HandCoinsIcon,
    InfinityIcon,
    SealPercentIcon,
    StethoscopeIcon,
    SubtitlesSlashIcon,
    TruckIcon,
} from "@phosphor-icons/react/ssr";
import { HeroFeature, HeroImages, PcosBubbleVideoSlide, SourceCard, StepItem } from "./types";
import MessagingCard from "./components/MessagingCard";
import LabReviewCard from "./components/LabReviewCard";
import MonitoringCard from "./components/MonitoringCard";
import Paragraph from "../Typography/Paragraph";
import StarRating from "~/componentsDS/StarRating";
import Heading from "../Typography/Heading";
import { ComparisonTableColumn, ComparisonTableRow } from "./sections/ComparisonTableSection";
import Image from "next/image";

export const DEFAULT_HERO_FEATURES: HeroFeature[] = [
    {
        title: "Compounded in a Licensed U.S. Pharmacy",
        description: "503A U.S. pharmacy. NABP, LegitScript & PCAB certified.",
        mobileTitle: "Made in U.S.A",
    },
    {
        title: "Clinician-Approved Prescription Treatment",
        description: "HSA/FSA eligible. Secure.",
        mobileTitle: "Clinician-Approved Prescription",
    },
    {
        title: "Bioidentical Estradiol + Micronized Progesterone",
        description: "Clinically proven & third-party tested.",
        mobileTitle: "Bioidentical Estradiol + Micronized Progesterone",
    },
];

export const DEFAULT_HERO_TOP_CONTENT = (
    <StarRating className="mb-5 md:mb-6 text-pureWhite" />
);

export const DEFAULT_HERO_TITLE = (
    <>
        Menopause weight gain Isn’t about what you’re eating.
        <br className="max-md:hidden" /> It’s about{" "}
        <Heading
            as="i"
            font="featureDisplay"
            className="text-[32px] font-[inherit] not-italic leading-[110%] tracking-[-0.32px] md:font-featureDisplay md:text-[36px] md:font-normal md:italic md:leading-none md:tracking-[-0.36px]"
        >
            estrogen.
        </Heading>
    </>
);

export const DEFAULT_HERO_DESCRIPTION = (
    <>
        When estrogen declines, your metabolism changes — how you
        <br className="max-md:hidden" />
        store fat, how you burn it, and the constant food noise in your brain. No amount of
        restricting fixes a hormonal system. Oestra does.
    </>
);

export const DEFAULT_HERO_IMAGES: HeroImages = {
    largeDesktop: "https://res.cloudinary.com/dirscuuhp/image/upload/v1786000431/1923_kk7i1x.webp",
    desktop: "https://res.cloudinary.com/dirscuuhp/image/upload/v1786000374/Hero_Image_V2_3_1_y9mm1k.webp",
    mobile: "https://res.cloudinary.com/dirscuuhp/image/upload/v1785314986/Hero_18_cjggsi.webp",
};

export const fitPoints = [
    "You're postpartum and haven't felt like yourself since",
    "Your mood, sleep, or energy have noticeably shifted",
    "You're experiencing hair loss, brain fog, or low libido",
    "You want bioidentical hormone support, not a synthetic fix",
    "You want long-term hormone balance",
];

export const symptomBubbles = [
    {
        text: "“Why do I feel so unlike myself?”",
        className: "max-[1270px]:top-[120px] left-[-40px] top-[80px]",
    },
    {
        text: "“I'm more anxious than I've ever been.”",
        className: "right-[-80px] top-[60px]",
    },
    {
        text: "“I love my baby, but I'm completely depleted.”",
        className: "left-[-30px] top-[330px] max-w-[244px]",
    },
    {
        text: "“My hair is falling out in handfuls.”",
        className: "left-[-10px] top-[510px] max-w-[246px]",
    },
    {
        text: "“I thought I'd feel better by now.”",
        className: "max-[1270px]:top-[450px] right-[-30px] top-[490px] max-w-[282px]",
    },
];

export const symptomBubblesMobile = [
    {
        text: "“I'm more anxious than I've ever been.”",
        wrapperClassName: "right-[0px] top-[30px]",
        className: "relative max-w-[320px] !left-0 !top-0 text-[16px]",
    },
    {
        text: "“I love my baby, but I'm completely depleted.”",
        wrapperClassName: "left-[10px] top-[76px]",
        className: "relative max-w-[244px] !left-0 !top-0 text-[16px]",
    },
    {
        text: "“Why do I feel so unlike myself?”",
        wrapperClassName: "left-[10px] bottom-[120px]",
        className: "relative max-w-[280px] !left-0 !top-0 text-[16px]",
    },
    {
        text: "“My hair is falling out in handfuls.”",
        wrapperClassName: "right-[6px] bottom-[54px]",
        className: "relative max-w-[243px] !left-0 !top-0 text-[16px]",
    },
];

export const pricingItems = [
    {
        id: "prescription-treatment",
        icon: <StethoscopeIcon weight="light" size={44} />,
        title: <>Prescription treatment</>,
    },
    {
        id: "shipping",
        icon: <TruckIcon weight="light" size={44} />,
        title: <>Free & fast shipping</>,
    },
    {
        id: "clinical-follow-up",
        icon: <InfinityIcon weight="light" size={44} />,
        title: <>Unlimited clinical follow-up</>,
    },
    {
        id: "hsa-fsa",
        icon: <CheckCircleIcon weight="light" size={44} />,
        title: <>HSA/FSA Eligible</>,
    },
    {
        id: "cancel-anytime",
        icon: <SubtitlesSlashIcon weight="light" size={44} />,
        title: <>No insurance needed</>,
    },
    {
        id: "dose-adjustments",
        icon: <FadersHorizontalIcon weight="light" size={44} />,
        title: <>Dose adjustments if needed*</>,
    },
    {
        id: "money-back",
        icon: <HandCoinsIcon weight="light" size={44} />,
        title: <>Cancel <br className="md:hidden" /> anytime</>,
    },
    {
        id: "fullscript-discount",
        icon: <SealPercentIcon weight="light" size={44} />,
        title: <>20% off any supplement in FullScript</>,
    },
];

export const SOURCE_CARDS: SourceCard[] = [
    {
        id: "estradiol_progesterone",
        title: "Bioidentical Estradiol &\nMicronized Progesterone",
        image: "https://res.cloudinary.com/dirscuuhp/image/upload/c_crop,w_1664,h_1829,x_0,y_343/v1775561687/new_design/oestra_source_1.webp",
        imageAlt: "Bioidentical estradiol and progesterone icon representing Oestra's hormone ingredients",
        description:
            "Molecularly identical to the hormones your \nbody naturally \nproduces.",
    },
    {
        id: "active_ingredients",
        title: "Pharmaceutical-Grade\nActive Ingredients",
        mobileTitle: "Pharma-Grade\nActive Ingredients",
        image: "https://res.cloudinary.com/dirscuuhp/image/upload/c_crop,w_2769,h_3057,x_631,y_1/v1775561829/new_design/oestra_source_2.webp",
        imageAlt: "Pharmaceutical-grade active ingredients icon for Inner Balance Oestra quality standards",
        description:
            "Sourced from FDA-inspected U.S. manufacturers. Compounded by a Licensed U.S. \nPharmacy.",
    },
    {
        id: "potency_testing",
        title: "Third-Party Potency Testing",
        image: "https://res.cloudinary.com/dirscuuhp/image/upload/c_crop,w_1956,h_2221,x_405,y_629/v1775561829/new_design/oestra_source_3.webp",
        imageAlt: "Third-party potency testing icon confirming Inner Balance Oestra quality and safety",
        description:
            "Independent labs confirm the strength and \naccuracy of every compounded \nformula.",
    },
    {
        id: "ph_balanced_cream",
        title: "Clean, PH-Balanced Cream",
        mobileTitle: "Clean, \nPH-Balanced Cream",
        image: "https://res.cloudinary.com/dirscuuhp/image/upload/c_crop,w_1948,h_1773,x_0,y_510/v1775561687/new_design/oestra_source_4.webp",
        imageAlt: "Clean pH-balanced cream icon representing Oestra's vaginal-safe hormone delivery formula",
        description:
            "Fragrance-free, paraben-free, and designed for \noptimal \nabsorption.",
    },
    {
        id: "clinician_guided_dosing",
        title: "Clinician-Guided Dosing",
        image: "https://res.cloudinary.com/dirscuuhp/image/upload/c_crop,w_1792,h_1817,x_0,y_245/v1775561687/new_design/oestra_source_5.webp",
        imageAlt: "Clinician-guided dosing icon representing Inner Balance's personalized hormone therapy approach",
        description:
            "Dose increases are based on your symptoms and response. Labs are optional. Your treatment is guided by clinical evaluation and how you feel.",
        descriptionMobile: "Dose increases are based on your symptoms and response. Treatment is guided by clinical evaluation and how you feel.",
    },
];

export const hiwData: StepItem[] = [
    {
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1781599121/new_design/Frame_1171278096_8_stepfirst_ieds2u.webp",
        icon: <QuizStep className="w-[110px] h-[110px] md:w-[150px] md:h-[150px]" />,
        title: "Quiz Intake",
        analyticTitle:"Quiz Intake",
        description: "You share your symptoms, history and goals.",
    },
    {
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1779461093/new_design/Frame_1171278096_11_kiojpd.webp",
        icon: <ReceiveTreatmentStep className="w-[110px] h-[110px] md:w-[150px] md:h-[150px]" />,
        title: "Clinical approval for treatment delivery",
        analyticTitle:"Clinical approval for treatment delivery",
        description: "A licensed clinician evaluates and determines next steps. Once approved, prescription is delivered.",
    },
    {
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1781598807/new_design/Frame_1171278096_10_hmciycyc.webp",
        icon: <StartTreatmentStep className="w-[110px] h-[110px] md:w-[150px] md:h-[150px]" />,
        title: <>Start treatment and <br/> get ongoing support</>,
        analyticTitle:"Start treatment and get ongoing support",
        description: "Our clinical care team is always here for you whenever you want to check in.",
    },
];

export const notAloneData = [
    {
        id: "messaging",
        component: <MessagingCard />,
        title: "Unlimited secured messaging"
    },
    {
        id: "lab-review",
        component: <LabReviewCard />,
        title:"Lab review and dose adjustments if needed"
    },
    {
        id: "monitoring",
        component: <MonitoringCard />,
        title: "Ongoing monitoring — not one-time prescribing."
    },
]

export const TREATMENTS_AND_SAFETY = [
    {
        id: 1,
        question: "Is Oestra® safe to use?",
        answer: (
            <>
                Yes. Oestra<sup>®</sup> contains estradiol and progesterone—bioidentical
                hormones that are FDA-approved for use in treating hormone deficiency.
                It’s also delivered vaginally, a method backed by over 100 high-quality
                studies showing stable hormone levels, systemic benefits, and protection
                of the uterine lining. Safety isn’t just a promise—it’s clinically
                supported.
            </>
        ),
    },
    {
        id: 2,
        question: "Is Oestra® safe to use long-term?",
        answer: (
            <>
                Yes. Hormone therapy is one of the most extensively studied treatments
                in medicine, with decades of research supporting its safety when used
                under medical supervision. Oestra<sup>®</sup> uses bioidentical
                estradiol and micronized progesterone — hormones identical to those
                naturally produced by your body — to support healthy hormone balance.
                If you ever choose to stop treatment, you can do so at any time. Your
                hormone levels will simply return to their natural baseline. Our
                medical team monitors your treatment and guides adjustments to ensure
                it remains safe and effective for you.
            </>
        ),
    },
    {
        id: 3,
        question: "Is Oestra® third-party tested?",
        answer: (
            <>
                It goes beyond that. Oestra<sup>®</sup> is made by a 503B FDA-regulated
                compounding pharmacy that’s inspected by the FDA, DEA, and state
                licensing boards. Every prescription batch undergoes rigorous potency,
                purity, and safety checks before it ever reaches your door.
            </>
        ),
    },
    {
        id: 4,
        question: "Are compounding pharmacies safe?",
        answer: (
            <>
                Yes, when they’re held to the highest regulatory standards—as ours is.
                Our partner pharmacy is LegitScript-certified, meaning it meets
                stringent quality and safety criteria. You’re getting the best of
                personalized medicine with pharmaceutical-grade oversight.
            </>
        ),
    },
    {
        id: 5,
        question: "Is Oestra® bioidentical or synthetic?",
        answer: (
            <>
                Oestra<sup>®</sup> uses only bioidentical hormones—compounds identical
                to the ones your body used to make, derived from plant sources. In
                plain terms: they’re natural to your body, better tolerated than
                synthetics, and designed to restore—not override—your natural balance.
            </>
        ),
    },
    {
        id: 6,
        question: "How is Oestra® different from other hormone replacement therapies?",
        answer: (
            <>
                Two major ways:
                <br />
                <br />
                – Delivery: Oestra<sup>®</sup> is a vaginal cream, allowing for faster
                absorption and more stable hormone levels than oral pills or topical
                patches.
                <br />
                <br />
                – Formulation: It’s an all-in-one treatment. One pump a day delivers
                estradiol, progesterone, and testosterone (via conversion), saving you
                from juggling 4–6 separate prescriptions.
            </>
        ),
    },
    {
        id: 7,
        question: "Does it work better than supplements?",
        answer: (
            <>
                Yes. Herbal supplements may temporarily ease symptoms, but they can’t
                restore hormone levels. Your body doesn’t make Dong Quai or Chaste
                Berry—it makes hormones. Oestra<sup>®</sup> gives your body what it
                actually needs, in the correct ratios, with prescription-level quality
                and precision.
            </>
        ),
    },
    {
        id: 8,
        question: "Why don’t you require labs before starting?",
        answer: (
            <>
                No. Lab work is not required to begin treatment. Your care is guided
                first and foremost by your symptoms, medical history, and a
                comprehensive clinical review — not just lab numbers. Many hormone
                imbalances can be identified clinically, and you can begin treatment
                without testing. If you’d like deeper insight, we do offer convenient
                lab testing, and a full clinical review of your results is included in
                your care. Labs are optional — your journey with us will be
                personalized either way. You can start feeling better now, and add
                testing later if and when it makes sense for you.
            </>
        ),
    },
    {
        id: 9,
        question: "What if I was told I have estrogen dominance? Will this make it worse?",
        answer: (
            <>
                No. In fact, Oestra<sup>®</sup> is designed to help correct estrogen
                dominance — not cause it. Estrogen dominance often occurs when
                progesterone levels are too low relative to estrogen. Oestra
                <sup>®</sup> provides both progesterone and estradiol in a carefully
                balanced ratio to help restore hormonal balance and calm the
                fluctuations that can drive symptoms. Because the hormones in Oestra
                <sup>®</sup> are delivered vaginally, they are absorbed efficiently and
                avoid the liver metabolism that can sometimes contribute to hormonal
                imbalances. This allows for more stable hormone levels and a more
                balanced response. Our medical team also personalizes dosing and
                monitors your progress to ensure your treatment remains balanced and
                effective for your body.
            </>
        ),
    },
    {
        id: 10,
        question: "What are the side effects?",
        answer: (
            <>
                Most women tolerate Oestra<sup>®</sup> very well. As your body adjusts
                to hormone optimization, some women may experience mild breast
                tenderness or light cramping during the first few weeks. These symptoms
                are typically temporary and resolve quickly. Less common side effects
                may occur but are usually mild and easily addressed with simple
                adjustments to dosing or supportive recommendations. Our medical care
                team monitors your progress and is here to guide you every step of the
                way, ensuring your treatment remains comfortable, effective, and
                personalized to your needs.
            </>
        ),
    },
    {
        id: 11,
        question: "Will this affect my ability to have kids?",
        answer: (
            <>
                No — Oestra<sup>®</sup> is non-contraceptive and does not suppress
                ovulation.
            </>
        ),
    },
    {
        id: 12,
        question: "Can I stay on this long-term?",
        answer: (
            <>
                Yes. Bioidentical hormones are safe to take for decades and actually
                support longevity and disease prevention.
            </>
        ),
    },
];

export const GETTING_PRESCRIBED = [
    {
        id: 1,
        question: "How do I get started?",
        answer: (
            <>
                Start by taking our health quiz. If you’re a candidate, you’ll move
                straight to checkout. After a licensed clinician reviews your info,
                your prescription is sent to the pharmacy and shipped directly to you.
                You’ll receive a 3-month supply as part of a monthly subscription.
            </>
        ),
    },
    {
        id: 2,
        question: "Do I need a prescription for Oestra®?",
        answer: (
            <>
                Yes. Every Oestra<sup>®</sup> prescription is reviewed and approved by
                a licensed clinician. You don’t need to book a separate appointment -
                your quiz responses help us determine if it’s safe to begin treatment
                and ensure you&apos;re a good candidate for our starting dose.
            </>
        ),
    },
    {
        id: 3,
        question: "How is Inner Balance’s approach to HRT different?",
        answer: (
            <>
                We don’t isolate hormones - we balance them. Most HRT programs target
                estrogen, progesterone, or testosterone in isolation. Oestra
                <sup>®</sup> is different. It supports all three hormones together in
                the right ratios, to restore balance without the usual trial-and-error
                or side effects.
            </>
        ),
    },
];

export const PRODUCT_USE = [
    {
        id: 1,
        question: "How do you use Oestra®?",
        answer: (
            <>
                Apply one pearl-sized pump of cream daily to vaginal tissue using your
                finger. An applicator will be available in the future, but for now, we
                recommend manual application for best results.
            </>
        ),
    },
    {
        id: 2,
        question: "How long does Oestra® take to work?",
        answer: (
            <>
                You may start noticing changes within the first 2–4 weeks—especially
                around mood, sleep, and vaginal dryness. Deeper shifts like energy,
                skin, and metabolism take longer, often 3–6 months or more. Oestra
                <sup>®</sup> isn’t a quick fix; it’s a full-body recalibration. But
                when balance returns, it’s worth the wait.
            </>
        ),
    },
    {
        id: 3,
        question: "How often is Oestra® sent?",
        answer: (
            <>
                Oestra<sup>®</sup> is delivered every 3 months, and billed monthly.
                Your membership includes treatment, pharmacy processing, shipping, and
                unlimited clinical support.
            </>
        ),
    },
];

export const ELIGIBILITY_AND_ACCESS = [
    {
        id: 1,
        question: "Do you have to be in menopause to start Oestra®?",
        answer: (
            <>
                Not at all. We work with women as young as 18. In fact, many women
                start in their 30s or 40s—often before traditional medicine recognizes
                hormone decline. The earlier you begin, the more protection you provide
                your bones, brain, heart, skin, and metabolism.
            </>
        ),
    },
    {
        id: 2,
        question: "Is Oestra® covered by insurance?",
        answer: (
            <>
                It’s not—because insurance typically only covers the most limited
                options. Oestra<sup>®</sup> replaces 5–6 prescriptions and is still
                more cost-effective than most HRT plans. HSA/FSA is also accepted at
                checkout.
            </>
        ),
    },
    {
        id: 3,
        question: "Do you accept HSA/FSA for payment?",
        answer: (
            <>
                Yes, we accept HSA/FSA directly at checkout. Most carriers accept
                prescriptions as eligible and we also provide a letter of medical
                necessity.
            </>
        ),
    },
];

export const SHIPPING_AND_DELIVERY = [
    {
        id: 1,
        question: "Do you offer free shipping?",
        answer: (
            <>
                Yes. Shipping is always free—every Oestra<sup>®</sup> prescription
                ships at no extra cost.
            </>
        ),
    },
    {
        id: 2,
        question: "When will my order ship?",
        answer: (
            <>
                Most orders ship within 3 business days after your prescription is
                approved. We like to give your pharmacy time to prepare everything
                safely.
            </>
        ),
    },
    {
        id: 3,
        question: "Is the packaging discreet?",
        answer: (
            <>
                Yes. Your order arrives in discreet packaging.
                The outside of the box is labeled Inner Balance,
                but it does not identify the medication or indicate what’s inside.
            </>
        ),
    },
];

export const faqGroups = [
    { title: "TREATMENTS & SAFETY", items: TREATMENTS_AND_SAFETY, accordionClassName: "whitespace-pre-line mb-8" },
    { title: "GETTING PRESCRIBED", items: GETTING_PRESCRIBED, accordionClassName: "mb-8" },
    { title: "PRODUCT USE", items: PRODUCT_USE, accordionClassName: "mb-8" },
    { title: "ELIGIBILITY & ACCESS", items: ELIGIBILITY_AND_ACCESS, accordionClassName: "mb-8" },
    { title: "SHIPPING & DELIVERY", items: SHIPPING_AND_DELIVERY, accordionClassName: "" },
];

export const combinedFAQs = [
    ...TREATMENTS_AND_SAFETY,
    ...GETTING_PRESCRIBED,
    ...PRODUCT_USE,
    ...ELIGIBILITY_AND_ACCESS,
    ...SHIPPING_AND_DELIVERY,
];

export const SCIENCE_HORMONE_ID = {
    PROGESTERONE: "progesterone",
    ESTRADIOL: "estradiol",
    ELLAGE_BASE: "ellage-base",
    TESTOSTERONE: "testosterone",
} as const;

export type ScienceHormoneId = typeof SCIENCE_HORMONE_ID[keyof typeof SCIENCE_HORMONE_ID];

export const scienceHormones = [
    {
        id: SCIENCE_HORMONE_ID.PROGESTERONE,
        title: <>Bioidentical <br />Progesterone</>,
        description: <>Protects the uterine lining, promotes restful sleep, balances estradiol, and supports a calm nervous system.</>,
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1774697508/treatment-1_47196a7fa8.webp",
        mobileImageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1774697508/treatment-1_47196a7fa8.webp",
        imageAlt: "Bioidentical progesterone ingredient in Oestra®",
        info: {
            subtitle: "Hormone Support",
            title: "Bioidentical progesterone for protection and balance",
            badges: ["Bioidentical", "uterine support", "calming support"],
            titleAdditional: "Ingredient Sourcing",
            description: [
                "Compounded using pharmaceutical-grade ingredients sourced from US manufacturers.",
                "Third-party labs verify that the strength in the bottle matches the prescription on the label.",
                "Our active ingredients arrive with a Certificate of Analysis to confirm 100% purity."
            ]
        }
    },
    {
        id: SCIENCE_HORMONE_ID.ESTRADIOL,
        title: <>Bioidentical <br />Estradiol</>,
        description: <>Supports brain, bones, metabolism,<br className="hidden xl:block"/> mood,  vaginal tissue, and long-term<br className="hidden xl:block"/>  vitality.</>,
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1774697521/treatment-2_47195edbe5.webp",
        mobileImageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1774697521/treatment-2_47195edbe5.webp",
        imageAlt: "Bioidentical estradiol ingredient in Oestra®",
        info: {
            subtitle: "Hormone Support",
            title: "Bioidentical estradiol — the body’s most active estrogen",
            badges: ["Bioidentical", "systemic effect", "plant-derived"],
            titleAdditional: "Ingredient Sourcing",
            description: [
                "Compounded using pharmaceutical-grade ingredients sourced from US manufacturers.",
                "Third-party labs verify that the strength in the bottle matches the prescription on the label.",
                "Our active ingredients arrive with a Certificate of Analysis to confirm 100% purity."
            ]
        }
    },
    {
        id: SCIENCE_HORMONE_ID.ELLAGE_BASE,
        title: <>Ellage<sup>®</sup> Anhydrous <br className="hidden md:block" />Base</>,
        description: "Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels.",
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1777278896/new_design/Treatment_card_new_3_nssauq.webp",
        mobileImageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1777278896/new_design/Treatment_card_new_3_nssauq.webp",
        imageAlt: "Ellagé® anhydrous base for vaginal absorption",
        info: {
            subtitle: "Delivery System",
            title: "Ellage® anhydrous base for enhanced absorption",
            badges: ["anhydrous", "pH-balanced", "stable delivery"],
            titleAdditional: "Why it matters",
            description: [
                "100% paraben-free, fragrance-free and contains no endocrine disruptors.",
                "Water-free and designed for sensitive vaginal tissue.",
                "Enhances absorption, prevents leakage, and maintains natural pH for consistent systemic hormone levels."
            ]
        }
    },
    {
        id: SCIENCE_HORMONE_ID.TESTOSTERONE,
        title: <>Supports Natural Testosterone</>,
        description: <>Progesterone serves as a precursor to <br />testosterone, supporting libido, <br />motivation, strength, and energy — <br />naturally and without another product.</>,
        mobileDescription: "Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels.",
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775563492/new_design/Treatment_card_new_lwrc54.webp",
        mobileImageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1777219816/new_design/Treatment_card_new_gxf2dj.webp",
        imageAlt: "",
        info: {
            subtitle: "Hormone Pathway",
            title: "Progesterone supports natural testosterone production",
            badges: ["No Synthetic Testosterone", "self-regulated", "downstream conversion"],
            titleAdditional: "Why no added testosterone?",
            description: [
                "Progesterone is a precursor to testosterone.",
                "Restoring it allows your body to regulate its own optimal levels — supporting libido, strength, and energy naturally."
            ]
        }
    },
];

export const PcosBubbleSliderData = [
    {
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1779111086/new_design/Rectangle_34624292_1_o4pmbs.webp",
        title: ["Your hormones","shifted first"],
        mediaType: "video",
        analyticsTitle: "Your hormones shifted first",
        mobImageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1779116098/new_design/Rectangle_5_8_hhhlqi.webp",
        videoUrl: "https://res.cloudinary.com/dirscuuhp/video/upload/v1779787025/new_design/Evergreen_Kling_First_Last_Frame_2026-05-04_07-44-20_3_1_kk9tpp.mp4"
    },
    {
        videoUrl: "https://res.cloudinary.com/dirscuuhp/video/upload/v1779786888/new_design/Evergreen_Kling_First_Last_Frame_2026-05-04_08-14-57_1_1_dut52r.mp4",
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1779111213/new_design/Rectangle_34624291_3_cayixz.webp",
        title: ["Your body started producing"," more testosterone"],
        mediaType: "video",
        analyticsTitle: "Your body started producing more testosterone"
    },
    {
        videoUrl:"https://res.cloudinary.com/dirscuuhp/video/upload/v1779786702/new_design/Balls_Evergreen_jumgdb.mp4",
        imageUrl: "https://res.cloudinary.com/dirscuuhp/image/upload/v1779111273/new_design/Rectangle_34624290_1_ivqaux.webp",
        title: ["That’s what made weight loss", "nearly impossible"],
        mediaType: "video",
        analyticsTitle: "That’s what made weight loss nearly impossible",
    },
] satisfies PcosBubbleVideoSlide[];

export const carouselDefaultHeaderElement = <Heading
    as="h2"
    size="sm"
    sizes={{ md: "md" }}
    className="mb-4 text-center text-[32px] text-primary500 max-md:mx-auto max-md:max-w-[320px] max-md:leading-[1.1] md:text-[48px]"
>
        PMOS/PCOS is
    <span className="font-featureDisplay text-[28px] font-normal italic md:text-[43px]">
        {" "}not
    </span>{" "}
    caused by weight gain
</Heading>

export const carouselDefaultSubTitle = "You’ve likely been told that losing weight will fix your PMOS/PCOS."
export const carouselDefaultDescription = <Paragraph className="text-center text-[16px] font-semibold leading-[1.3] text-primary500 md:text-[18px]">
    You can’t fix PMOS/PCOS by losing weight if your hormones are the reason you can’t lose weight.
</Paragraph>

export const solutionPoints = [
    {
        title: "Restore progesterone and estradiol",
        positionClassName: "max-lg:max-w-[240px] left-[46%] top-[15%] lg:left-[83%] lg:top-[15%]",
    },
    {
        title: "Reduce inflammation",
        positionClassName: "left-[77%] top-[40%] lg:left-[97%] lg:top-[40%]",
    },
    {
        title: "Support the uterine lining",
        positionClassName: "left-[75%] top-[65%] lg:left-[95.5%] lg:top-[65%]",
    },
    {
        title: "Help regulate your cycle",
        positionClassName: "left-[35%] top-[90%] lg:left-[78%] lg:top-[90%]",
    },
];

export const defaultCTAHeader = (
    <>
        <Heading
            as="h2"
            className="
                hidden md:block
                mt-8 max-w-[520px]
                text-center text-[32px] font-light leading-[1.1] tracking-[-0.4px]
                md:max-w-[620px] md:text-left md:text-[40px]
            "
        >
            Think you might have PMOS/PCOS — <br />
            or not getting{" "}
            <span className="font-featureDisplay text-[28px] font-normal italic md:text-[36px]">
                real
            </span>{" "}
            answers?
        </Heading>

        <Heading
            as="h2"
            className="
                mt-8 max-w-[520px]
                text-center text-[32px] font-light leading-[1.08] tracking-[-0.04em]
                md:hidden
                md:max-w-[620px] md:text-left md:text-[40px]
            "
        >
            Hormone{" "}
            <span className="font-featureDisplay text-[28px] italic md:text-[40px]">
                balance.
            </span>{" "}
            <br />
            Simplified.
        </Heading>
    </>
);

export const defaultComparisonBadge = "Why Our Formula?";

export const defaultComparisonTitle = (
    <>
        Why waxing or laser{" "}
        <i className="font-featureDisplay font-normal italic">
            fails
        </i>
    </>
);

export const defaultComparisonSubtitle =
    "Removing hair isn’t the same as treating why it grows";

export const defaultComparisonDescription = null;

export const defaultComparisonColumns: ComparisonTableColumn[] = [
    {
        id: "bodymatched",
        image: (
            <Image
                src="https://res.cloudinary.com/dirscuuhp/image/upload/v1784539600/Anti-aging_Face_Cream_1_1_g5nfid.webp"
                alt="BodyMatched"
                width={40}
                height={140}
                sizes="(max-width: 768px) 40px, 40px"
                className="h-[140px] w-[40px] object-contain"
            />
        ),
        className:
            "max-w-[258px] rounded-2xl bg-gradient-to-b from-faceCream500 to-[#8DB4D4] text-pureWhite shadow-xl",
        cellClassName: "text-pureWhite",
    },
    {
        id: "eflornithine",
        image: (
            <Image
                src="https://res.cloudinary.com/dirscuuhp/image/upload/v1784298232/Treatment_card_new_8_1_1_vdoak3.webp"
                alt="Eflornithine"
                width={130}
                height={130}
                sizes="130px"
                className="h-[130px] w-[130px] object-contain"
            />
        ),
        cellClassName: "text-primary500",
    },
    {
        id: "hair-removal",
        image: (
            <Image
                src="https://res.cloudinary.com/dirscuuhp/image/upload/v1784790023/Treatment_card_new_9_1_qgcygm.webp"
                alt="Hair removal"
                width={130}
                height={130}
                sizes="130px"
                className="h-[130px] w-[130px] object-contain"
            />
        ),
        cellClassName: "text-primary500",
    },
];

export const defaultComparisonRows: ComparisonTableRow[] = [
    {
        id: "product-title",
        values: {
            bodymatched: "Finasteride (BodyMatched®)",
            eflornithine: "Eflornithine (Vaniqa)",
            "hair-removal": "Waxing / Threading / Laser",
        },
    },
    {
        id: "what-it-does",
        title: "What it does",
        values: {
            bodymatched:
                "Addresses the cause — blocks DHT at the follicle, slowing regrowth at the source.",
            eflornithine:
                "Slows visible regrowth rate at the skin surface. Does not change the hormonal driver.",
            "hair-removal":
                "Removes existing hair. Regrowth\n returns at the same rate.",
        },
    },
    {
        id: "cause-addressed",
        title: "Cause addressed?",
        values: {
            bodymatched: "Yes — hormonal signal",
            eflornithine: "Partially — surface-level",
            "hair-removal": "No — cosmetic removal only",
        },
    },
    {
        id: "mechanism",
        title: "Mechanism",
        values: {
            bodymatched:
                "Blocks DHT production at the hormonal source",
            eflornithine:
                "Inhibits an enzyme in the follicle to slow regrowth",
            "hair-removal":
                "Physically removes hair at the\n surface",
        },
    },
    {
        id: "long-term-outcome",
        title: "Long-term outcome",
        values: {
            bodymatched:
                "Targets the hormonal cause — many see continued improvement the longer it’s used, not just paused growth",
            eflornithine:
                "Only masks symptoms — growth resumes immediately once stopped, no lasting change",
            "hair-removal":
                "Hair returns every 2–6 weeks —\n same routine forever",
        },
    },
];

export const defaultComparisonDecoration = null;
